<template>
  <o-heading>
    <template #left>
      <div class="flex items-center">
        <o-avatar :items="[customer]" class="hidden md:block mr-6 md:mr-4 flex-shrink-0" size="2xl" rounded="rounded-3xl" />
        <div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-3 pb-2">
              <p class="text-xl tracking-tight font-bold leading-tight/85 sm:text-2xl sm:truncate">
                {{ customer.displayName }}
              </p>

              <template v-if="customer.isBirthdayToday">
                <o-tooltip :text="$t('general.birthday', { name: customer.displayName })">
                  <span class="text-2xl tracking-tighter">🎉</span>
                </o-tooltip>
              </template>
            </div>
            <div class="flex flex-col md:flex-row md:flex-wrap opacity-80 leading-tight mb-2">
              <div v-if="customer.company" class="flex items-center space-x-2 mb-2 mr-4">
                <o-icon icon="company" :size="14" />
                <p class="text-sm">
                  {{ customer.company }}
                </p>
              </div>
              <div v-if="customer.phone" class="flex items-center space-x-2 mb-2 mr-4">
                <o-icon icon="phone" :size="14" />
                <o-dropdown>
                  <p class="text-sm text-primary-600 cursor-pointer">
                    {{ customer.phone }}
                  </p>

                  <template #content>
                    <o-dropdown-item :description="$t('customer.callInfo')" :href="'tel:'+customer.phone">
                      {{ $t('customer.call') }}
                    </o-dropdown-item>

                    <o-dropdown-item v-if="$grants.all({ marketing: ['send'] })" tag-name="OPanelLink" :description="$t('customer.sendSMSInfo')" path="/message/send/sms" :props="{ customers: [ customer ], template }">
                      {{ $t('general.sendSMS') }}
                    </o-dropdown-item>
                  </template>
                </o-dropdown>
              </div>
              <div v-if="customer.homePhone" class="flex items-center space-x-2 mb-2 mr-4">
                <o-icon icon="homePhone" :size="14" />
                <p class="text-sm">
                  <a :href="'tel:'+customer.homePhone" class="text-primary-600">
                    {{ customer.homePhone }}
                  </a>
                </p>
              </div>
              <div v-if="customer.email" class="flex items-center space-x-2 mb-2 mr-4">
                <o-icon icon="email" :size="14" />
                <p class="text-sm">
                  <o-panel-link path="/message/send/email" :props="{ customers: [ customer ], template }" class="text-primary-600">
                    {{ customer.email }}
                  </o-panel-link>
                </p>
              </div>
              <div v-if="customer.addressLabel" class="flex items-center space-x-2 mb-2 mr-4">
                <o-icon icon="address" :size="14" />
                <p class="text-sm">
                  <a :href="`https://www.google.com/maps/search/?api=1&query=${customer.addressLabel}`" target="_blank">
                    {{ customer.addressLabel }}
                  </a>
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center">
              <customer-alerts :customer="customer" class="mb-1 mr-1 cursor-pointer" @click="handleAlertClick" />

              <template v-if="customer.segments && customer.segments.length">
                <template v-for="segment in customer.segments" :key="segment.idSegment">
                  <o-chip

                    :variant="segment.variant"
                    class="mr-1 mb-1"
                    size="sm"
                  >
                    {{ segment.shortName }}
                  </o-chip>
                </template>
              </template>
              <o-chip v-if="isCustomerPrivacyConsentAccepted" variant="success" class="mb-1 mr-1" size="sm">
                {{ $t('components.customer.profile.heading.dataCollectionConsented') }}
              </o-chip>
              <o-chip v-if="hasBouncedEmail" variant="warning" class="mb-1 mr-1" size="sm">
                {{ $t('general.emailBounced') }}
              </o-chip>

              <o-chip v-if="isDeleted" variant="danger" class="mb-1 mr-1" size="sm">
                {{ $t('general.deleted') }}
              </o-chip>

              <o-chip v-if="isArchived" variant="warning" class="mb-1 mr-1" size="sm">
                {{ $t('general.archived') }}
              </o-chip>

              <o-chip
                v-if="customer.ageVerified"
                 class="mb-1 mr-1"
                size="sm"
                variant="success"
                icon="success"
              >
                {{ $t('components.customer.profile.details.ageVerified') }} - {{ customer.ageVerifiedDetail.verifiedViaLabel }}
              </o-chip>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-if="!panel" #right>
      <Actions
        :customer="customer"
        @update:customer="$emit('update:customer', $event)"
        @update:loading="$emit('update:loading', $event)"
      />
      <UnblockEmailDialog
        ref="unblockEmailDialog"
        :customer="customer"
        @update:customer="$emit('update:customer', $event)"
      />
    </template>
  </o-heading>
</template>

<script setup lang="ts">
import CustomerAlerts from '@/components/customer/elements/Alerts.vue'
import Actions from '@/components/customer/profile/actions.vue'
import UnblockEmailDialog from '@/components/customer/actions/UnblockEmail.vue'

import {
  OAvatar,
  ODropdown,
  ODropdownItem,
  ODropdownDivider,
} from '@/components/elements'

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
  (e: 'update:loading', value: any): void
  (e: 'selectTab', tab: string): void
}>()

const panel = inject('panel', null)
const unblockEmailDialog = ref<any>()

const template = computed(() => {
  return {
    models: [
      'Customer',
      'Location',
    ],
  }
})

const isCustomerPrivacyConsentAccepted = computed(() => props.customer.isCustomerPrivacyConsentAccepted)
const isArchived = computed(() => props.customer.isArchived)
const isDeleted = computed(() => props.customer.isDeleted)
const hasAlerts = computed(() => props.customer.hasAlerts)
const hasBouncedEmail = computed(() => !!props.customer.emailBounced)

const handleAlertClick = () => {
  emit('selectTab', 'notes')
}

const handleUnblockEmail = () => {
  unblockEmailDialog.value?.open()
}
</script>
